/**
 * First-game coach (REDESIGN.md 7.10): dims the table except one spotlighted
 * area, Coach Tibbs points at it with one short sentence. Steps advance on the
 * player's own action; the last one closes on a tap. The × skips it all.
 */
import { useLayoutEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Tibbs } from '../Art';
import { IconClose } from '../Icons';

export interface CoachStep {
  n: 1 | 2 | 3 | 4;
  text: string;
  /** data-coach values to spotlight (their union). */
  spot: string[];
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

function measure(spot: string[]): Box | null {
  let l = Infinity;
  let t = Infinity;
  let r = -Infinity;
  let b = -Infinity;
  for (const k of spot) {
    document.querySelectorAll<HTMLElement>(`[data-coach="${k}"]`).forEach((el) => {
      const rc = el.getBoundingClientRect();
      if (!rc.width) return;
      l = Math.min(l, rc.left);
      t = Math.min(t, rc.top);
      r = Math.max(r, rc.right);
      b = Math.max(b, rc.bottom);
    });
  }
  if (!Number.isFinite(l)) return null;
  const pad = 8;
  return { left: l - pad, top: t - pad, width: r - l + pad * 2, height: b - t + pad * 2 };
}

export function Coach({ step, onSkip, onDone }: { step: CoachStep; onSkip: () => void; onDone: () => void }) {
  const [box, setBox] = useState<Box | null>(null);
  const key = step.spot.join(',');
  useLayoutEffect(() => {
    let raf = 0;
    const update = () => setBox(measure(step.spot));
    update();
    const iv = setInterval(update, 400);
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    window.addEventListener('resize', on);
    return () => {
      clearInterval(iv);
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', on);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!box) return null;
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const below = box.top + box.height + 120 < vh && box.top < vh * 0.45;
  const bubbleStyle: CSSProperties = {
    left: Math.max(12, Math.min(vw - 12 - Math.min(340, vw - 24), box.left + box.width / 2 - 170)),
    width: Math.min(340, vw - 24),
    ...(below ? { top: box.top + box.height + 12 } : { bottom: vh - box.top + 12 }),
  };
  const closeOnTap = step.n === 4;
  return (
    <div className={`coach${closeOnTap ? ' tap-close' : ''}`} onClick={closeOnTap ? onDone : undefined}>
      <div className="coach-spot" style={box} />
      <div className={`coach-bubble${below ? ' below' : ' above'}`} style={bubbleStyle} role="dialog" aria-live="polite" aria-label="Tip">
        <Tibbs pose="point" size={64} className="coach-tibbs" />
        <p className="coach-text">{step.text}</p>
        <button
          type="button"
          className="icon-btn coach-x"
          aria-label="Skip the tour"
          onClick={(e) => {
            e.stopPropagation();
            onSkip();
          }}
        >
          <IconClose size={18} />
        </button>
      </div>
    </div>
  );
}
